(() => {
  if (customElements.get('pt-love-wall')) return;
  customElements.define('pt-love-wall', class extends HTMLElement {
    connectedCallback() {
      if (this.dataset.continuation === 'true' || this.dataset.editor === 'true') return;
      this.grid = this.querySelector('.pt-love-grid');
      if (!this.grid) return;
      this.sources = [{ wall: this, grid: this.grid, cards: [...this.grid.children] }];
      const walls = [...document.querySelectorAll('pt-love-wall')];
      for (let i = walls.indexOf(this) + 1; i < walls.length && walls[i].dataset.continuation === 'true'; i++) {
        const wall = walls[i], grid = wall.querySelector('.pt-love-grid');
        this.sources.push({ wall, grid, cards: [...grid.children] });
        wall.hidden = true;
      }
      this.cards = this.sources.flatMap(source => source.cards);
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.small = matchMedia('(max-width: 600px)');
      this.medium = matchMedia('(max-width: 1000px)');
      this.paused = false;
      this.static = false;
      this.manual = false;
      this.pointers = new Set();
      this.positions = new WeakMap();
      this.last = 0;
      this.tick = this.tick.bind(this);
      this.querySelector('[data-love-layout]').addEventListener('click', () => {
        this.static = !this.static;
        this.build();
      }, options);
      [this.reduced, this.small, this.medium].forEach(query => query.addEventListener('change', () => this.build(), options));
      this.visible = new IntersectionObserver(entries => { this.dataset.offscreen = String(!entries[0].isIntersecting); this.syncMotion(); });
      this.visible.observe(this);
      document.addEventListener('visibilitychange', () => {
        this.dataset.background = String(document.hidden);
        if (document.hidden) { this.pointers.clear(); this.resumeAfterReading(); }
        this.syncMotion();
      }, options);
      const release = event => {
        if (this.pointers.delete(event.pointerId)) this.resumeAfterReading();
      };
      document.addEventListener('pointerup', release, options);
      document.addEventListener('pointercancel', release, options);
      this.build();
    }
    build() {
      cancelAnimationFrame(this.motionFrame);
      this.motionFrame = 0;
      clearTimeout(this.resumeTimer);
      this.manual = false;
      this.pointers.clear();
      this.dataset.interacting = 'false';
      this.laneAbort?.abort();
      this.laneAbort = new AbortController();
      this.resize?.disconnect();
      this.grid.replaceChildren(...this.cards);
      const moving = this.dataset.autoplay === 'true' && !this.reduced.matches && !this.static && this.cards.length > 1;
      this.dataset.moving = String(moving);
      this.querySelector('.pt-love-controls').hidden = this.dataset.autoplay !== 'true' || this.reduced.matches || this.cards.length < 2;
      const layout = this.querySelector('[data-love-layout]');
      layout.textContent = this.static ? 'Show scrolling wall' : 'View all at once';
      layout.setAttribute('aria-pressed', String(this.static));
      if (!moving) return;
      const columns = Math.min(this.cards.length, this.small.matches ? 1 : this.medium.matches ? 2 : 3);
      this.grid.replaceChildren();
      for (let i = 0; i < columns; i++) {
        const lane = document.createElement('div'); lane.className = 'pt-love-lane';
        lane.tabIndex = 0;
        lane.setAttribute('role', 'region');
        lane.setAttribute('aria-label', `Scrollable customer reviews, column ${i + 1} of ${columns}`);
        const track = document.createElement('div'); track.className = 'pt-love-track';
        const group = document.createElement('div'); group.className = 'pt-love-group';
        this.cards.filter((_, n) => n % columns === i).forEach(card => group.append(card));
        const copy = () => {
          const clone = group.cloneNode(true);
          clone.setAttribute('aria-hidden', 'true');
          clone.querySelectorAll('a,button').forEach(el => el.tabIndex = -1);
          clone.querySelectorAll('[data-shopify-editor-block]').forEach(el => el.removeAttribute('data-shopify-editor-block'));
          return clone;
        };
        track.append(copy(), group, copy()); lane.append(track); this.grid.append(lane);
        lane.addEventListener('wheel', event => {
          event.preventDefault();
          const distance = event.deltaMode === 1 ? event.deltaY * 16 : event.deltaMode === 2 ? event.deltaY * lane.clientHeight : event.deltaY;
          lane.scrollTop -= distance;
          this.pauseForReading();
        }, { passive: false, signal: this.laneAbort.signal });
        lane.addEventListener('keydown', () => this.pauseForReading(), { signal: this.laneAbort.signal });
        lane.addEventListener('pointerdown', event => {
          this.pointers.add(event.pointerId);
          this.pauseForReading(false);
        }, { passive: true, signal: this.laneAbort.signal });
        // Native touch momentum keeps postponing resume until scrolling settles.
        // Automatic scroll events never start a manual interaction themselves.
        lane.addEventListener('scroll', () => {
          if (this.manual) {
            this.normalizeLane(lane);
            this.positions.set(lane, lane.scrollTop);
            this.resumeAfterReading();
          }
        }, { passive: true, signal: this.laneAbort.signal });
      }
      this.resize = new ResizeObserver(() => {
        this.grid.querySelectorAll('.pt-love-lane').forEach(lane => {
          const height = lane.querySelector('.pt-love-group').getBoundingClientRect().height;
          lane.dataset.loopHeight = String(height);
          if (!lane.dataset.positioned && height) {
            lane.scrollTop = height;
            this.positions.set(lane, height);
            lane.dataset.positioned = 'true';
          }
        });
      });
      this.grid.querySelectorAll('.pt-love-group:not([aria-hidden])').forEach(group => this.resize.observe(group));
      this.last = 0;
      this.syncMotion();
    }
    syncMotion() {
      cancelAnimationFrame(this.motionFrame);
      this.motionFrame = 0;
      this.last = 0;
      if (this.canAnimate()) this.motionFrame = requestAnimationFrame(this.tick);
    }
    canAnimate() {
      return this.dataset.moving === 'true' && !this.manual && this.dataset.offscreen !== 'true' && !document.hidden;
    }
    normalizeLane(lane) {
      const height = Number(lane.dataset.loopHeight);
      if (!height) return;
      if (lane.scrollTop >= height * 2) lane.scrollTop -= height;
      else if (lane.scrollTop <= 1) lane.scrollTop += height;
    }
    pauseForReading(scheduleResume = true) {
      this.manual = true;
      this.dataset.interacting = 'true';
      clearTimeout(this.resumeTimer);
      if (scheduleResume) this.resumeAfterReading();
    }
    resumeAfterReading() {
      clearTimeout(this.resumeTimer);
      if (!this.manual || this.pointers.size) return;
      this.resumeTimer = setTimeout(() => {
        this.manual = false;
        this.dataset.interacting = 'false';
        for (const lane of this.grid.querySelectorAll('.pt-love-lane')) this.positions.set(lane, lane.scrollTop);
        this.syncMotion();
      }, 1000);
    }
    tick(now) {
      this.motionFrame = 0;
      if (!this.canAnimate()) { this.last = 0; return; }
      const delta = this.last ? Math.min(64, now - this.last) / 1000 : 0;
      this.last = now;
      for (const lane of this.grid.querySelectorAll('.pt-love-lane')) {
        const height = Number(lane.dataset.loopHeight);
        if (!height) continue;
        let position = this.positions.get(lane) ?? lane.scrollTop;
        if (position >= height * 2) position -= height;
        else if (position <= 1) position += height;
        position += (Number(this.dataset.speed) || 16) * delta;
        this.positions.set(lane, position);
        lane.scrollTop = position;
      }
      this.motionFrame = requestAnimationFrame(this.tick);
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.motionFrame); clearTimeout(this.resumeTimer);
      this.abort?.abort(); this.laneAbort?.abort(); this.visible?.disconnect(); this.resize?.disconnect();
      this.sources?.forEach(source => { source.grid.replaceChildren(...source.cards); source.wall.hidden = false; });
    }
  });
})();
